import { InjectionToken, Signal } from '@angular/core';
import { DeepSignal } from '@ngrx/signals';
import { Observable } from 'rxjs';

import { LocalizedValue } from '@celum/core';
import { FormatFilters, FormatState } from '@celum/formats/domain';

/**
 * Minimal shape `FormatViewDialogBaseComponent` needs from the entity it's editing (`Format`/`BaseFormat`).
 */
export interface FormatViewLike {
  id?: number;
  key?: string;
  specificationReference?: { id?: number; key?: string };
  status?: { state: string };
  protected?: boolean;
}

/**
 * Minimal shape required from the entity store backing the dialog and the list (`FormatStore`/`BaseFormatStore`).
 */
export interface FormatStoreLike<TFormat extends FormatViewLike> {
  loading: Signal<boolean>;
  firstLoadCompleted: Signal<boolean>;
  entities: Signal<TFormat[]>;
  pagination: DeepSignal<{ totalCount: number; page: number; size: number; hasNext: boolean }>;
  selectedFormat: Signal<TFormat | undefined>;
  selectedFormatId: Signal<number>;
  filters: Signal<FormatFilters>;
  updateSelectedFormatId(id: number): void;
  loadFormatById(id: number): Observable<TFormat>;
  updateBatchSize(size: number): void;
  loadFormatsBatch(options: { isFirstLoad: boolean; removeEntities: boolean }): void;
  loadNextFormatsBatch(): void;
  addFormat(format: TFormat): void;
  updateFilters(filters: FormatFilters): void;
  deleteFormat(id: number): void;
  reset(isLoading?: boolean): void;
}

/**
 * Alias token for whichever entity store is provided in the current route (`FormatStore` for the tenant-scoped
 * formats route, `BaseFormatStore` for the base-format admin route - see `formatsRouteProviders()`/
 * `baseFormatsRouteProviders()`). Lets components shared between both routes (e.g. `SpecificationStore`,
 * `FormatsFilterComponent`, `FormatsFilterChipsComponent`) inject "the current format store" without knowing/caring
 * which concrete class backs it, instead of requiring it as an `@Input()` or depending on a concrete class that's
 * only provided on one of the two routes.
 */
export const FORMAT_STORE = new InjectionToken<FormatStoreLike<FormatViewLike>>('FORMAT_STORE');

/**
 * Minimal shape `AbstractFormatsTable` needs from the entity rendered in a row (`Format`/`BaseFormat`).
 */
export interface FormatTableEntityLike extends FormatViewLike {
  id: number;
  name: LocalizedValue;
  description?: { locales?: LocalizedValue };
  icon?: { url?: string };
  status: {
    state: FormatState;
    errorMessage?: string;
    errorKey?: string;
  };
  protected: boolean;
}

/**
 * Minimal shape required from the CRUD service backing the dialog (`FormatsService`/`BaseFormatsService`).
 */
export interface FormatServiceLike<TFormat extends FormatViewLike, TCreate, TUpdate> {
  createFormat(format: TCreate): Observable<TFormat>;
  updateFormat(id: number, format: TUpdate): Observable<TFormat>;
}

/**
 * Minimal shape required from the form service backing the dialog (`FormatFormService`/`BaseFormatFormService`).
 */
export interface FormatFormServiceLike<TCreate> {
  formatParametersChanged?: Signal<boolean>;
  warningDialogDisabled: boolean;
  generateFormatObject(): TCreate;
  disableDialogWarning(): void;
}
